import { PATIENT_STATUSES, type PatientStatus, type TrackerEntryDto } from './patientTrackingTypes';

const KANBAN_ARCHIVE_STATUSES = new Set<PatientStatus>(['Resolved', 'Lost']);
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export interface KanbanLayoutConfig {
  order: PatientStatus[];
  hidden: PatientStatus[];
}

export function kanbanColumnOrderStorageKey(stateStorageKey: string): string {
  return `${stateStorageKey}:kanban-column-order`;
}

export function kanbanLayoutStorageKey(stateStorageKey: string): string {
  return `${stateStorageKey}:kanban-layout`;
}

/** Merge a stored column order with the canonical status list (handles new/removed statuses). */
export function normalizeKanbanColumnOrder(value: unknown): PatientStatus[] {
  const allowed = new Set<string>(PATIENT_STATUSES);
  const incoming = Array.isArray(value)
    ? value.filter((s): s is PatientStatus => typeof s === 'string' && allowed.has(s))
    : [];
  const seen = new Set(incoming);
  const merged = [...incoming];
  for (const status of PATIENT_STATUSES) {
    if (!seen.has(status)) merged.push(status);
  }
  return merged;
}

function normalizeKanbanHidden(value: unknown): PatientStatus[] {
  const allowed = new Set<string>(PATIENT_STATUSES);
  if (!Array.isArray(value)) return [];
  return value.filter((s): s is PatientStatus => typeof s === 'string' && allowed.has(s));
}

export function normalizeKanbanLayout(value: unknown): KanbanLayoutConfig {
  if (!value || typeof value !== 'object') {
    return { order: [...PATIENT_STATUSES], hidden: [] };
  }
  const raw = value as Partial<KanbanLayoutConfig>;
  return {
    order: normalizeKanbanColumnOrder(raw.order),
    hidden: normalizeKanbanHidden(raw.hidden)
  };
}

export function readKanbanLayout(stateStorageKey: string): KanbanLayoutConfig {
  try {
    const layoutRaw = localStorage.getItem(kanbanLayoutStorageKey(stateStorageKey));
    if (layoutRaw) return normalizeKanbanLayout(JSON.parse(layoutRaw));

    const legacyRaw = localStorage.getItem(kanbanColumnOrderStorageKey(stateStorageKey));
    return {
      order: legacyRaw ? normalizeKanbanColumnOrder(JSON.parse(legacyRaw)) : [...PATIENT_STATUSES],
      hidden: []
    };
  } catch {
    return { order: [...PATIENT_STATUSES], hidden: [] };
  }
}

export function writeKanbanLayout(stateStorageKey: string, config: KanbanLayoutConfig): void {
  try {
    localStorage.setItem(kanbanLayoutStorageKey(stateStorageKey), JSON.stringify(config));
    localStorage.setItem(
      kanbanColumnOrderStorageKey(stateStorageKey),
      JSON.stringify(config.order)
    );
  } catch {
    // localStorage can be unavailable in private browsing.
  }
}

export function readKanbanColumnOrder(stateStorageKey: string): PatientStatus[] {
  return readKanbanLayout(stateStorageKey).order;
}

export function writeKanbanColumnOrder(stateStorageKey: string, order: PatientStatus[]): void {
  const current = readKanbanLayout(stateStorageKey);
  writeKanbanLayout(stateStorageKey, { ...current, order });
}

/** Kanban-only: hide resolved/lost cards older than one week (grid still shows all rows). */
export function isVisibleInKanbanColumn(
  entry: TrackerEntryDto,
  columnStatus: PatientStatus,
  nowMs = Date.now()
): boolean {
  if (!KANBAN_ARCHIVE_STATUSES.has(columnStatus)) return true;
  const modifiedMs = Date.parse(entry.modified);
  if (Number.isNaN(modifiedMs)) return true;
  return nowMs - modifiedMs <= ONE_WEEK_MS;
}

export function filterKanbanColumnEntries(
  entries: TrackerEntryDto[],
  columnStatus: PatientStatus,
  nowMs = Date.now()
): TrackerEntryDto[] {
  return entries.filter((entry) => isVisibleInKanbanColumn(entry, columnStatus, nowMs));
}

export function reorderKanbanColumns(
  order: PatientStatus[],
  fromIndex: number,
  toIndex: number
): PatientStatus[] {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= order.length || toIndex >= order.length) {
    return order;
  }
  const next = [...order];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
}
